import { useState, useEffect } from 'react';
import { RotateCcw, AlertCircle, Shield } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  usePermissions,
  type Permission,
  type LocationPermissions,
} from '@/hooks/usePermissions';
import { cn } from '@/lib/utils';

interface LocationPermissionEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userRole: string;
  locationId: string;
  locationName: string;
}


export function LocationPermissionEditor({
  open,
  onOpenChange,
  userId,
  userRole,
  locationId,
  locationName,
}: LocationPermissionEditorProps) {
  const {
    permissionsByCategory,
    rolePermissions,
    updateLocationPermission,
    resetLocationPermissions,
    getUserLocationPermissions,
  } = usePermissions();

  const [locationOverrides, setLocationOverrides] = useState<Record<string, boolean>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Load existing location permissions
  useEffect(() => {
    if (open && userId && locationId) {
      loadLocationPermissions();
    }
  }, [open, userId, locationId]);

  const loadLocationPermissions = async () => {
    try {
      const locPerms = await getUserLocationPermissions(userId);
      const current = locPerms.find((lp: LocationPermissions) => lp.location_id === locationId);
      if (current && current.permissions) {
        setLocationOverrides(current.permissions);
      } else {
        setLocationOverrides({});
      }
      setHasChanges(false);
    } catch (error) {
      console.error('Error loading location permissions:', error);
    }
  };

  // Get role default for a permission
  const getRoleDefault = (permissionCode: string): boolean => {
    const rolePerms = rolePermissions[userRole] || [];
    return rolePerms.includes(permissionCode) || rolePerms.includes('*');
  };

  // Check if permission has an override
  const hasOverride = (permissionCode: string): boolean => {
    return permissionCode in locationOverrides;
  };

  // Get current value (override or role default)
  const getCurrentValue = (permissionCode: string): boolean => {
    if (hasOverride(permissionCode)) {
      return locationOverrides[permissionCode];
    }
    return getRoleDefault(permissionCode);
  };

  // Handle permission toggle
  const handleToggle = (permissionCode: string, value: boolean) => {
    const roleDefault = getRoleDefault(permissionCode);

    if (value === roleDefault) {
      // Remove override if setting back to default
      const newOverrides = { ...locationOverrides };
      delete newOverrides[permissionCode];
      setLocationOverrides(newOverrides);
    } else {
      // Set override
      setLocationOverrides(prev => ({
        ...prev,
        [permissionCode]: value,
      }));
    }
    setHasChanges(true);
  };

  // Reset a single permission to default
  const handleResetPermission = (permissionCode: string) => {
    const newOverrides = { ...locationOverrides };
    delete newOverrides[permissionCode];
    setLocationOverrides(newOverrides);
    setHasChanges(true);
  };

  // Reset all permissions
  const handleResetAll = async () => {
    try {
      setIsSaving(true);
      await resetLocationPermissions.mutateAsync({ userId, locationId });
      setLocationOverrides({});
      setHasChanges(false);
    } catch (error) {
      console.error('Error resetting permissions:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Save all changes
  const handleSave = async () => {
    try {
      setIsSaving(true);

      // First reset all, then set new overrides
      await resetLocationPermissions.mutateAsync({ userId, locationId });

      // Set each override
      for (const [code, granted] of Object.entries(locationOverrides)) {
        await updateLocationPermission.mutateAsync({
          userId,
          locationId,
          permissionCode: code,
          granted,
        });
      }

      setHasChanges(false);
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving permissions:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const overrideCount = Object.keys(locationOverrides).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Permissions at {locationName}
          </DialogTitle>
          <DialogDescription>
            Override permissions for this user at this specific location.
            Permissions not overridden will use the default for the{' '}
            <Badge variant="outline" className="mx-1">{userRole}</Badge> role.
          </DialogDescription>
        </DialogHeader>

        {overrideCount > 0 && (
          <div className="flex items-center justify-between px-4 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <div className="flex items-center gap-2 text-sm text-amber-600">
              <AlertCircle className="h-4 w-4" />
              <span>{overrideCount} permission{overrideCount !== 1 ? 's' : ''} overridden at this location</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-amber-600 hover:text-amber-700"
              onClick={handleResetAll}
              disabled={isSaving}
            >
              <RotateCcw className="h-3 w-3 mr-1" />
              Reset All
            </Button>
          </div>
        )}

        <ScrollArea className="h-[500px] pr-4">
          <div className="space-y-6">
            {Object.entries(permissionsByCategory).map(([category, perms]) => (
              <div key={category}>
                <h4 className="text-sm font-medium capitalize mb-3 sticky top-0 bg-background py-1">
                  {category.replace('_', ' ')}
                </h4>
                <div className="space-y-2">
                  {(perms as Permission[]).map((perm) => {
                    const roleDefault = getRoleDefault(perm.code);
                    const isOverridden = hasOverride(perm.code);
                    const currentValue = getCurrentValue(perm.code);

                    return (
                      <div
                        key={perm.id}
                        className={cn(
                          "flex items-center justify-between p-3 rounded-lg border transition-colors",
                          isOverridden
                            ? "bg-amber-500/5 border-amber-500/20"
                            : "bg-card"
                        )}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Label className="font-medium text-sm cursor-pointer">
                              {perm.name}
                            </Label>
                            {isOverridden && (
                              <Badge variant="secondary" className="text-xs bg-amber-500/20 text-amber-600">
                                Override
                              </Badge>
                            )}
                          </div>
                          {perm.description && (
                            <p className="text-xs text-muted-foreground mt-0.5 truncate">
                              {perm.description}
                            </p>
                          )}
                          <div className="text-xs text-muted-foreground mt-1">
                            Role default:{' '}
                            <span className={roleDefault ? 'text-green-600' : 'text-red-600'}>
                              {roleDefault ? 'Allowed' : 'Denied'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 ml-4">
                          {isOverridden && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => handleResetPermission(perm.code)}
                              title="Reset to default"
                            >
                              <RotateCcw className="h-3 w-3" />
                            </Button>
                          )}
                          <Switch
                            checked={currentValue}
                            onCheckedChange={(checked) => handleToggle(perm.code, checked)}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <Separator className="mt-4" />
              </div>
            ))}
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default LocationPermissionEditor;
